export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues?: { path: string; message: string }[];
  constructor(
    status: number,
    code: string,
    message: string,
    issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

type Listener = (e: ApiError) => void;
const listeners = new Set<Listener>();
/** Session-level failures (signed out, MFA needed) are routed globally. */
export const onAuthError = (l: Listener) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/**
 * Sensitive actions need a fresh step-up (a TOTP code or a passkey). The app registers a
 * handler that asks for one; when it succeeds the request is sent again, once.
 */
let stepUpHandler: (() => Promise<boolean>) | null = null;
export const onStepUp = (h: () => Promise<boolean>) => {
  stepUpHandler = h;
  return () => {
    if (stepUpHandler === h) stepUpHandler = null;
  };
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { "x-recoup": "1" };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: payload,
    credentials: "same-origin",
  });
  if (res.status === 204) return undefined as T;
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (!res.ok) {
    const err = new ApiError(
      res.status,
      data?.error?.code ?? "error",
      data?.error?.message ?? "Something went wrong.",
      data?.error?.issues,
    );
    if (["signed_out", "mfa_required", "mfa_setup_required"].includes(err.code))
      for (const l of listeners) l(err);
    if (err.code === "step_up_required" && !retried && stepUpHandler && (await stepUpHandler()))
      return request<T>(method, path, body, true);
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, b: unknown = {}) => request<T>("POST", p, b),
  patch: <T>(p: string, b: unknown) => request<T>("PATCH", p, b),
  put: <T>(p: string, b: unknown) => request<T>("PUT", p, b),
  del: <T>(p: string) => request<T>("DELETE", p),
  upload: <T>(p: string, form: FormData) => request<T>("POST", p, form),
};
